import React, {
  useEffect, useState, useCallback, useRef,
} from 'react'

import axios from 'axios'
import {
  string, func, array, object, bool,
} from 'prop-types'

import { EMPTY_ARRAY, EMPTY_OBJECT, NOOP } from 'Utils/stableDefaults'

import ModelSearch from './ModelSearch'

function ModelSearchWithWrapper({
  onKeywordClickCallback = null,
  onDeleteTag = NOOP,
  onClear = NOOP,
  selectedFilters = EMPTY_OBJECT,
  initialTags = EMPTY_ARRAY,
  onFiltersChange = NOOP,
  allowTags = false,
  placeholder = '',
}) {
  const [tags, setTags] = useState(initialTags)
  const [isSearchSuggestionList, setIsSearchSuggestionList] = useState(null)
  const currentRequest = useRef(null)
  const requestCounter = useRef(0)
  const isOnClientSide = typeof window !== 'undefined'

  // Cleanup function to cancel any pending requests
  useEffect(() => {
    return () => {
      if (currentRequest.current) {
        currentRequest.current.cancel()
      }
    }
  }, [])

  async function handleSuggestions(inputValue) {
    try {
      requestCounter.current += 1
      const currentRequestId = requestCounter.current

      // Cancel previous request if it exists
      if (currentRequest.current) {
        currentRequest.current.cancel()
      }

      // Create a new cancel token
      const cancelToken = axios.CancelToken.source()
      currentRequest.current = cancelToken

      const response = await axios.get('/api/internal/items/live_search.json', {
        params: {
          query: inputValue,
        },
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
        },
        cancelToken: cancelToken.token,
        timeout: 3000,
      })

      // Only process if this is still the most recent request
      if (currentRequestId !== requestCounter.current) {
        return {
          keywords: [],
          searchKeyword: inputValue,
        }
      }

      const suggestionData = Array.isArray(response.data?.data)
        ? response.data.data
        : response.data

      setIsSearchSuggestionList(response.data?.new_search_suggestion_list)

      if (Array.isArray(suggestionData)) {
        const keywordSuggestions = suggestionData.map(
          ([suggestedKeyword, data]) => ({
            suggestedKeyword,
            thumbnailUrl: data.thumbnailUrl,
          })
        )
        return {
          keywords: [...keywordSuggestions],
          searchKeyword: inputValue,
        }
      }

      return {
        ...suggestionData,
        searchKeyword: inputValue,
      }
    } catch (error) {
      if (!axios.isCancel(error)) {
        // eslint-disable-next-line no-console
        console.error('Error fetching search suggestions:', error)
      }
      return {
        keywords: [],
        searchKeyword: inputValue,
      }
    }
  }

  const handleDifferentTypesOfKeywords = () => {
    if (Array.isArray(selectedFilters.keywords)) {
      return selectedFilters.keywords.map((keyword) => keyword)
    }

    return selectedFilters.keywords.split(',')
  }

  const processKeywordsValue = useCallback(
    ({ action, value }) => {
      const currentKeywordsValue = selectedFilters.keywords
        ? handleDifferentTypesOfKeywords()
        : []

      if (action === 'add') {
        return [...currentKeywordsValue, value].join(',')
      }

      if (action === 'remove') {
        return currentKeywordsValue
          .filter((keyword) => keyword !== value)
          .join(',')
      }

      return ''
    },
    [selectedFilters.keywords]
  )

  const onKeywordClick = ({ suggestedKeyword }) => {
    if (onKeywordClickCallback) {
      onKeywordClickCallback(suggestedKeyword)

      return
    }

    if (
      !selectedFilters.keywords ||!selectedFilters.keywords.includes(suggestedKeyword)
    ) {
      const newKeywordsQuery = processKeywordsValue({
        action: 'add',
        value: suggestedKeyword,
      })

      return onFiltersChange('keywords', newKeywordsQuery)
    }

    return null
  }

  const onDeleteTagClick = useCallback(
    ({ value: valueToDelete }) => {
      const newKeywordsQuery = processKeywordsValue({
        action: 'remove',
        value: valueToDelete,
      })

      onDeleteTag()

      onFiltersChange('keywords', newKeywordsQuery)
    },
    [selectedFilters.keywords]
  )

  const onClearAllClick = () => {
    onClear()

    return onFiltersChange('keywords', '')
  }

  const fetchSuggestions = async (keywords) => {
    const { data: suggestionsWithImages } = await axios.get(
      '/api/internal/tags/suggest_with_images',
      {
        params: { tags: keywords },
      }
    )

    return suggestionsWithImages
  }

  const checkIfKeywordIsEnabled = (keyword) => {
    if (selectedFilters.keywords) {
      return selectedFilters.keywords.split(',').includes(keyword)
    }

    return false
  }

  const parseSuggestions = (suggestionsWithImages) => suggestionsWithImages
    .map((keyword) => ({
      value: keyword[0],
      enabled: checkIfKeywordIsEnabled(keyword[0]),
      image: keyword[1].thumbnailUrl,
      onDeselectClick: onDeleteTagClick,
      onSelectClick: () => onKeywordClick({ suggestedKeyword: keyword[0] }),
    }))
    .sort((a, b) => {
      if (a.enabled < b.enabled) {
        return 1
      }
      if (a.enabled > b.enabled) {
        return -1
      }

      return 0
    })
    .sort((a, b) => {
      const selectedKeywords = selectedFilters.keywords.split(',')
      const aIdx = selectedKeywords.indexOf(a.value)
      const bIdx = selectedKeywords.indexOf(b.value)

      if (aIdx !== -1 && bIdx === -1) {
        return -1
      }
      if (bIdx !== -1 && aIdx === -1) {
        return 1
      }
      if (aIdx !== -1 && bIdx !== -1) {
        return aIdx - bIdx
      }
      return 0
    })

  const loadTags = async () => {
    if (selectedFilters.keywords && selectedFilters.keywords.length) {
      if (Array.isArray(selectedFilters.keywords)) {
        const suggestionsWithImages = await fetchSuggestions(
          selectedFilters.keywords
        )
        const tagsWithImages = parseSuggestions(suggestionsWithImages)

        return setTags(tagsWithImages)
      }

      const keywords = selectedFilters.keywords.split(',')
      const suggestionsWithImages = await fetchSuggestions(keywords)
      const tagsWithImages = parseSuggestions(suggestionsWithImages)

      return setTags(tagsWithImages)
    }

    return setTags([])
  }

  useEffect(() => {
    if (isOnClientSide && allowTags) {
      loadTags()
    }
  }, [isOnClientSide, selectedFilters.keywords, allowTags])

  return (
    <ModelSearch
      searchCallback={handleSuggestions}
      onKeywordClick={onKeywordClick}
      onClearAllClick={onClearAllClick}
      onDeleteTagClick={onDeleteTagClick}
      tags={allowTags ? tags : []}
      placeholder={placeholder}
      isSearchSuggestionList={isSearchSuggestionList}
    />
  )
}

ModelSearchWithWrapper.propTypes = {
  onKeywordClickCallback: func,
  onClear: func,
  onDeleteTag: func,
  placeholder: string,
  initialTags: array,
  onFiltersChange: func,
  selectedFilters: object,
  allowTags: bool,
}

export default ModelSearchWithWrapper
